import "@/App.css";
import "./reports/ReportStyles.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuditProvider } from "./context/AuditContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Phase1Context from "./pages/Phase1Context";
import Phase2Documents from "./pages/Phase2Documents";
import Phase3Controls from "./pages/Phase3Controls";
import Phase4Scope from "./pages/Phase4Scope";
import Phase5Artifacts from "./pages/Phase5Artifacts";
import Phase7EvidenceSqlControls from "./pages/Phase7EvidenceSqlControls";
// import Phase7EvidencePreview from "./pages/Phase7EvidencePreview";
import Phase7Testing from "./pages/Phase7Testing";
import Phase8Findings from "./pages/Phase8Findings";
import Phase9Classification from "./pages/Phase9Classification";
import Phase10Report from "./pages/Phase10Report";
import ModelConfig from "./pages/ModelConfig";
import AuditLogs from "./pages/AuditLogs";
import UserManagement from "./pages/UserManagement";
import Profile from "./pages/Profile";
import Integrations from "./pages/Integrations";
import { Loader2 } from "lucide-react";
import { ROUTES } from "./lib/constants";
import ReportPrintDocument from "./reports/ReportPrintDocument";

function ProtectedRoute({ children, roles, permissions }) {
  const { isAuthenticated, loading, user, hasAnyPermission } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
    </div>
  );
  if (!isAuthenticated) {
    console.log("[Auth] User is not authenticated. Redirecting to login.");
    return <Navigate to={ROUTES.LOGIN} replace />;
  }
  if (roles && user && !roles.includes(user.role)) {
    console.warn(`[Auth] User role '${user.role}' unauthorized. Redirecting to profile.`);
    return <Navigate to={ROUTES.PROFILE} replace />;
  }
  if (permissions && !hasAnyPermission(...permissions)) {
    console.warn("[Auth] User lacks permissions. Redirecting to profile.");
    return <Navigate to={ROUTES.PROFILE} replace />;
  }
  return children;
}

function AppRoutes() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
    </div>
  );

  return (
    <Routes>
      <Route path={ROUTES.LOGIN} element={isAuthenticated ? <Navigate to={ROUTES.PROFILE} replace /> : <Login />} />
      <Route path={ROUTES.ROOT} element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Navigate to={ROUTES.PROFILE} replace />} />
        <Route path={ROUTES.PHASE_1} element={<Phase1Context />} />
        <Route path={ROUTES.PHASE_2} element={<Phase2Documents />} />
        <Route path={ROUTES.PHASE_3} element={<Phase3Controls />} />
        <Route path={ROUTES.PHASE_4} element={<Phase4Scope />} />
        <Route path={ROUTES.PHASE_5} element={<Phase5Artifacts />} />
        <Route path={ROUTES.PHASE_7} element={<Phase7EvidenceSqlControls />} />
        {/* <Route path={ROUTES.PHASE_7_PREVIEW} element={<Phase7EvidencePreview />} /> */}
        <Route path={ROUTES.PHASE_7_TESTING} element={<Phase7Testing />} />
        <Route path={ROUTES.PHASE_8} element={<Phase8Findings />} />
        <Route path={ROUTES.PHASE_9} element={<Phase9Classification />} />
        <Route path={ROUTES.PHASE_10} element={<Phase10Report />} />
        <Route path={ROUTES.PROFILE} element={<Profile />} />
        <Route path={ROUTES.INTEGRATIONS} element={<Integrations />} />
        <Route path={ROUTES.CONFIG} element={<ProtectedRoute permissions={["model:config"]}><ModelConfig /></ProtectedRoute>} />
        <Route path={ROUTES.LOGS} element={<ProtectedRoute permissions={["logs:view"]}><AuditLogs /></ProtectedRoute>} />
        <Route path={ROUTES.USERS} element={<ProtectedRoute permissions={["users:manage"]}><UserManagement /></ProtectedRoute>} />
      </Route>
      <Route path="*" element={<Navigate to={ROUTES.PROFILE} replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <AuditProvider>
        <div id="app">
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </div>
        <ReportPrintDocument />
        <Toaster position="top-right" />
      </AuditProvider>
    </AuthProvider>
  );
}

export default App;
